"use client";
import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { useTranslations, useLocale } from "next-intl";
import { api, Progress, PatientProgress, SkillKey } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { PageHelp } from "@/components/PageHelp";
import { getToken } from "@/lib/auth";

const SKILLS: SkillKey[] = ["empathy", "reflection", "question_quality", "focus", "rapport"];
const COMPETENT_LINE = 60; // supervisor "solid / competent" threshold

// Stage order for a patient's progress map (untouched → processing).
const STAGES = ["untouched", "mentioned", "opened", "processing"] as const;
type Stage = (typeof STAGES)[number];

function scoreColor(score: number) {
  const hue = Math.max(0, Math.min(100, score)) * 1.2; // 0=red → 120=green
  return `hsl(${hue}, 70%, 45%)`;
}

function fmtDate(iso: string | null, locale: string) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(locale, { day: "numeric", month: "short" });
  } catch {
    return "";
  }
}

// Progress-map issue names can arrive as snake_case keys ("self_worth"); show them
// as plain, readable text with a leading capital (locale-aware for Turkish i/İ).
function prettifyLabel(s: string, locale: string) {
  const cleaned = s.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
  if (!cleaned) return cleaned;
  return cleaned.charAt(0).toLocaleUpperCase(locale) + cleaned.slice(1);
}

type TipLine = { text: string; bold?: boolean; size?: number; color?: string };

// In-SVG tooltip (viewBox coords, so it scales correctly with the responsive svg).
// Auto-flips below the point when there isn't room above, and clamps horizontally so
// it never overflows the chart. Parent svg needs style overflow:visible.
function SvgTooltip({ x, y, lines, vbWidth }: { x: number; y: number; lines: TipLine[]; vbWidth: number }) {
  const padX = 9, padY = 7, lineH = 15;
  const w = Math.max(...lines.map((l) => l.text.length * (l.size ?? 11) * 0.6)) + padX * 2;
  const h = lines.length * lineH + padY * 2;
  const bx = Math.max(2, Math.min(vbWidth - w - 2, x - w / 2));
  const by = y - h - 12 < 2 ? y + 14 : y - h - 12;
  return (
    <g pointerEvents="none">
      <rect x={bx} y={by} width={w} height={h} rx={7} fill="var(--surface)" stroke="var(--border)" strokeWidth={1} />
      {lines.map((l, i) => (
        <text
          key={i}
          x={bx + w / 2}
          y={by + padY + lineH * (i + 1) - 4}
          textAnchor="middle"
          fontSize={l.size ?? 11}
          fontWeight={l.bold ? 700 : 500}
          fill={l.color ?? "var(--text-primary)"}
        >
          {l.text}
        </text>
      ))}
    </g>
  );
}

// ── Composite line chart ──────────────────────────────────────────────
type LinePoint = { value: number; label?: string; sublabel?: string };
function LineChart({ series }: { series: LinePoint[] }) {
  const [hover, setHover] = useState<number | null>(null);
  const W = 640;
  const H = 240;
  const padL = 34;
  const padR = 16;
  const padT = 16;
  const padB = 26;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const n = series.length;
  const values = series.map((s) => s.value);

  const x = (i: number) => padL + (n <= 1 ? plotW / 2 : (i * plotW) / (n - 1));
  const y = (v: number) => padT + plotH * (1 - Math.max(0, Math.min(100, v)) / 100);

  const linePath = values.map((v, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(v)}`).join(" ");
  const areaPath = n > 1
    ? `${linePath} L ${x(n - 1)} ${y(0)} L ${x(0)} ${y(0)} Z`
    : "";

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="w-full h-auto"
      style={{ overflow: "visible" }}
      role="img"
      onMouseLeave={() => setHover(null)}
    >
      {/* y gridlines */}
      {[0, 25, 50, 75, 100].map((g) => (
        <g key={g}>
          <line x1={padL} x2={W - padR} y1={y(g)} y2={y(g)} stroke="var(--border)" strokeWidth={1} />
          <text x={padL - 8} y={y(g) + 4} textAnchor="end" fontSize={11} fill="var(--text-muted)">{g}</text>
        </g>
      ))}
      {/* competent reference line */}
      <line
        x1={padL} x2={W - padR} y1={y(COMPETENT_LINE)} y2={y(COMPETENT_LINE)}
        stroke="var(--accent)" strokeWidth={1.5} strokeDasharray="5 4" opacity={0.7}
      />
      {areaPath && <path d={areaPath} fill="var(--accent)" opacity={0.08} />}
      <path d={linePath} fill="none" stroke="var(--accent)" strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />
      {values.map((v, i) => (
        <circle key={i} cx={x(i)} cy={y(v)} r={hover === i ? 6 : 4} fill={scoreColor(v)} stroke="var(--surface)" strokeWidth={1.5} className="transition-all" />
      ))}
      {/* hover hit areas (wider than the visible dot so points are easy to catch) */}
      {values.map((v, i) => (
        <circle
          key={`hit-${i}`}
          cx={x(i)} cy={y(v)} r={16}
          fill="transparent"
          style={{ cursor: "pointer" }}
          onMouseEnter={() => setHover(i)}
          onMouseMove={() => setHover(i)}
        />
      ))}
      {hover !== null && (
        <SvgTooltip
          x={x(hover)}
          y={y(values[hover])}
          vbWidth={W}
          lines={[
            { text: String(values[hover]), bold: true, size: 15, color: scoreColor(values[hover]) },
            ...(series[hover].label ? [{ text: series[hover].label!, size: 11, color: "var(--text-secondary)" }] : []),
            ...(series[hover].sublabel ? [{ text: series[hover].sublabel!, size: 10, color: "var(--text-muted)" }] : []),
          ]}
        />
      )}
    </svg>
  );
}

// ── Skill rings (5 microskills, early-vs-recent via concentric dual arc) ──
function DeltaBadge({ value }: { value: number | null }) {
  if (value === null) return null;
  if (value === 0) return <span className="text-xs font-medium" style={{ color: "var(--text-muted)" }}>±0</span>;
  const up = value > 0;
  return (
    <span className="text-xs font-semibold tabular-nums" style={{ color: up ? "#16a34a" : "#dc2626" }}>
      {up ? "▲" : "▼"} {Math.abs(value)}
    </span>
  );
}

function SkillRing({
  recent, early, label, recentLabel, earlyLabel,
}: {
  recent: number | null;
  early: number | null;
  label: string;
  recentLabel: string;
  earlyLabel: string;
}) {
  const [hover, setHover] = useState(false);
  const size = 84;
  // Outer (thick) arc = recent; inner (thin) arc = earlier. Concentric so both
  // are always visible and the gap between them reads as growth/regression.
  const sOuter = 9;
  const sInner = 4;
  const rOuter = (size - sOuter) / 2;
  const rInner = rOuter - sOuter; // sit inside the outer ring with a small gap
  const cOuter = 2 * Math.PI * rOuter;
  const cInner = 2 * Math.PI * rInner;
  const rec = recent ?? 0;
  const ear = early ?? 0;
  const color = recent === null ? "var(--text-muted)" : scoreColor(rec);
  const delta = recent !== null && early !== null ? Math.round(rec - ear) : null;

  return (
    <div
      className="relative flex flex-col items-center gap-1.5"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          {/* outer track */}
          <circle cx={size / 2} cy={size / 2} r={rOuter} fill="none" stroke="var(--accent-light)" strokeWidth={sOuter} />
          {/* outer arc — recent */}
          <circle
            cx={size / 2} cy={size / 2} r={rOuter} fill="none" stroke={color} strokeWidth={sOuter}
            strokeLinecap="round" strokeDasharray={cOuter} strokeDashoffset={cOuter * (1 - rec / 100)}
            className="transition-all"
          />
          {/* inner arc — earlier (only when we have a prior period) */}
          {early !== null && (
            <>
              <circle cx={size / 2} cy={size / 2} r={rInner} fill="none" stroke="var(--border)" strokeWidth={sInner} />
              <circle
                cx={size / 2} cy={size / 2} r={rInner} fill="none" stroke="var(--text-muted)" strokeWidth={sInner}
                strokeLinecap="round" strokeDasharray={cInner} strokeDashoffset={cInner * (1 - ear / 100)} opacity={0.6}
              />
            </>
          )}
        </svg>
        <span
          className="absolute inset-0 flex items-center justify-center text-lg font-semibold tabular-nums"
          style={{ color, fontFamily: "var(--font-lora)" }}
        >
          {recent ?? "–"}
        </span>
      </div>
      <span className="text-xs text-center text-[var(--text-secondary)] leading-tight max-w-[88px]">{label}</span>
      <DeltaBadge value={delta} />

      {hover && (recent !== null || early !== null) && (
        <div
          className="absolute bottom-full mb-2 z-10 whitespace-nowrap rounded-lg border px-3 py-2 text-xs shadow-lg pointer-events-none"
          style={{ background: "var(--surface)", borderColor: "var(--border)" }}
        >
          <p className="font-semibold text-[var(--text-primary)] mb-0.5">{label}</p>
          <p style={{ color: "var(--accent)" }}>{recentLabel}: <span className="font-semibold tabular-nums">{recent ?? "–"}</span></p>
          {early !== null && (
            <p className="text-[var(--text-muted)]">{earlyLabel}: <span className="font-semibold tabular-nums">{early}</span></p>
          )}
        </div>
      )}
    </div>
  );
}

function SkillRings({
  recent, early, skillLabel, recentLabel, earlyLabel,
}: {
  recent: Partial<Record<SkillKey, number>>;
  early: Partial<Record<SkillKey, number>> | null;
  skillLabel: (k: SkillKey) => string;
  recentLabel: string;
  earlyLabel: string;
}) {
  return (
    <div className="grid grid-cols-3 sm:grid-cols-5 gap-x-3 gap-y-6 justify-items-center">
      {SKILLS.map((k) => (
        <SkillRing
          key={k}
          recent={typeof recent[k] === "number" ? recent[k]! : null}
          early={early && typeof early[k] === "number" ? early[k]! : null}
          label={skillLabel(k)}
          recentLabel={recentLabel}
          earlyLabel={earlyLabel}
        />
      ))}
    </div>
  );
}

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-2xl border p-4" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
      <p className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{label}</p>
      <p className="text-2xl font-semibold text-[var(--text-primary)] mt-1 tabular-nums" style={{ fontFamily: "var(--font-lora)" }}>
        {value}
      </p>
      {sub && <p className="text-xs text-[var(--text-secondary)] mt-0.5">{sub}</p>}
    </div>
  );
}

function Sparkline({ values }: { values: number[] }) {
  const [hover, setHover] = useState<number | null>(null);
  if (values.length === 0) return null;
  const W = 120;
  const H = 32;
  const n = values.length;
  const x = (i: number) => (n <= 1 ? W / 2 : (i * W) / (n - 1));
  const y = (v: number) => H - (H - 4) * (Math.max(0, Math.min(100, v)) / 100) - 2;
  const path = values.map((v, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(v)}`).join(" ");
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="w-[120px] h-8"
      style={{ overflow: "visible" }}
      role="img"
      onMouseLeave={() => setHover(null)}
    >
      <path d={path} fill="none" stroke="var(--accent)" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
      {(values.length === 1 || hover !== null) && (
        <circle cx={x(hover ?? 0)} cy={y(values[hover ?? 0])} r={3} fill="var(--accent)" />
      )}
      {values.map((v, i) => (
        <circle
          key={`hit-${i}`}
          cx={x(i)} cy={y(v)} r={8}
          fill="transparent"
          style={{ cursor: "pointer" }}
          onMouseEnter={() => setHover(i)}
          onMouseMove={() => setHover(i)}
        />
      ))}
      {hover !== null && (
        <SvgTooltip
          x={x(hover)}
          y={y(values[hover])}
          vbWidth={W}
          lines={[{ text: String(values[hover]), bold: true, size: 12, color: scoreColor(values[hover]) }]}
        />
      )}
    </svg>
  );
}

function PatientCard({ p }: { p: PatientProgress }) {
  const t = useTranslations("progress");
  const locale = useLocale();
  const [open, setOpen] = useState(false);
  const alliancePct = Math.round(Math.max(0, Math.min(1, p.alliance)) * 100);
  const trendValues = p.wellbeing_trend.map((pt) => pt.value);
  const trendSeries: LinePoint[] = p.wellbeing_trend.map((pt, i) => ({
    value: pt.value,
    label: t("patients.sessionNo", { n: i + 1 }),
    sublabel: fmtDate(pt.ended_at, locale),
  }));
  const mapEntries = Object.entries(p.progress_map || {});

  return (
    <div className="rounded-2xl border overflow-hidden" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-[var(--surface-alt)] transition-colors"
      >
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-[var(--text-primary)] truncate">
            {p.name} <span className="text-sm font-normal text-[var(--text-muted)]">· {p.age}</span>
          </p>
          <p className="text-xs text-[var(--text-secondary)] mt-0.5">
            {t("patients.sessions", { count: p.session_count })} · {t("patients.insights", { count: p.insights })}
          </p>
        </div>
        {/* alliance gauge */}
        <div className="hidden sm:block w-32 shrink-0">
          <div className="flex items-center justify-between text-[11px] mb-1">
            <span className="text-[var(--text-muted)]">{t("patients.alliance")}</span>
            <span className="font-semibold tabular-nums text-[var(--text-secondary)]">{alliancePct}%</span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--accent-light)" }}>
            <div className="h-full rounded-full" style={{ width: `${alliancePct}%`, background: "var(--accent)" }} />
          </div>
        </div>
        {/* patient wellbeing: current value + trend */}
        {p.wellbeing !== null && (
          <div className="hidden md:flex flex-col items-end shrink-0">
            <div className="flex items-center gap-1.5 text-[11px] mb-1">
              <span className="text-[var(--text-muted)]">{t("patients.wellbeing")}</span>
              <span className="font-semibold tabular-nums" style={{ color: scoreColor(p.wellbeing) }}>{p.wellbeing}</span>
            </div>
            {trendValues.length > 0 && <Sparkline values={trendValues} />}
          </div>
        )}
        <svg
          className={`w-5 h-5 shrink-0 text-[var(--text-muted)] transition-transform ${open ? "rotate-180" : ""}`}
          fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
        </svg>
      </button>

      {open && (
        <div className="px-5 pb-5 pt-1 border-t" style={{ borderColor: "var(--border)" }}>
          {/* alliance (mobile) */}
          <div className="sm:hidden mt-3">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-[var(--text-muted)]">{t("patients.alliance")}</span>
              <span className="font-semibold tabular-nums">{alliancePct}%</span>
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--accent-light)" }}>
              <div className="h-full rounded-full" style={{ width: `${alliancePct}%`, background: "var(--accent)" }} />
            </div>
          </div>

          {/* progress map */}
          <div className="mt-4">
            <p className="text-xs uppercase tracking-widest mb-2" style={{ color: "var(--text-muted)" }}>{t("patients.mapTitle")}</p>
            {mapEntries.length === 0 ? (
              <p className="text-sm text-[var(--text-muted)]">{t("patients.mapEmpty")}</p>
            ) : (
              <ul className="space-y-2">
                {mapEntries.map(([issue, stage]) => {
                  const idx = STAGES.indexOf(stage as Stage);
                  const safeIdx = idx < 0 ? 0 : idx;
                  return (
                    <li key={issue} className="flex items-center gap-3">
                      <span className="text-sm text-[var(--text-primary)] flex-1 min-w-0 truncate">{prettifyLabel(issue, locale)}</span>
                      <span className="flex items-center gap-1 shrink-0">
                        {STAGES.map((_, i) => (
                          <span
                            key={i}
                            className="w-5 h-1.5 rounded-full"
                            style={{ background: i <= safeIdx ? "var(--accent)" : "var(--accent-light)" }}
                          />
                        ))}
                      </span>
                      <span className="text-xs w-24 text-right shrink-0" style={{ color: "var(--accent-mid)" }}>
                        {t(`patients.stage.${(STAGES[safeIdx]) as Stage}`)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* patient wellbeing over time */}
          {trendValues.length > 0 && (
            <div className="mt-5">
              <p className="text-xs uppercase tracking-widest mb-2" style={{ color: "var(--text-muted)" }}>{t("patients.wellbeingTitle")}</p>
              <LineChart series={trendSeries} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ProgressPage() {
  const t = useTranslations("progress");
  const tSkill = useTranslations("feedbackReport.metrics");
  const locale = useLocale();
  const router = useRouter();
  const [data, setData] = useState<Progress | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!getToken()) { router.push("/login"); return; }
    api.getProgress()
      .then(setData)
      .catch(() => router.push("/login"))
      .finally(() => setLoading(false));
  }, [router]);

  const compositeSeries = useMemo<LinePoint[]>(
    () => (data?.overall.trend ?? []).map((p) => ({
      value: p.overall,
      label: p.case_name,
      sublabel: fmtDate(p.ended_at, locale),
    })),
    [data, locale],
  );
  const hasData = (data?.stats.evaluated_sessions ?? 0) > 0;

  const practiceLabel = useMemo(() => {
    const m = data?.stats.practice_minutes ?? 0;
    const h = Math.floor(m / 60);
    const min = m % 60;
    return h > 0 ? t("stats.practiceValue", { h, m: min }) : t("stats.practiceMinutes", { m: min });
  }, [data, t]);

  return (
    <AppShell active="progress">
      <main className="max-w-4xl mx-auto px-5 py-12">
        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-[var(--text-primary)]" style={{ fontFamily: "var(--font-lora)" }}>
              {t("title")}
            </h1>
            <p className="text-sm text-[var(--text-secondary)] mt-2">{t("subtitle")}</p>
          </div>
          <PageHelp topic="progress" />
        </div>

        {loading ? (
          <p className="text-sm text-[var(--text-muted)]">{t("loading")}</p>
        ) : !hasData ? (
          <div className="rounded-2xl border border-dashed p-10 text-center" style={{ borderColor: "var(--border)" }}>
            <p className="text-[var(--text-primary)] font-medium">{t("empty")}</p>
            <p className="text-sm text-[var(--text-secondary)] mt-2">{t("emptyHint")}</p>
            <a
              href="/dashboard"
              className="inline-block mt-5 text-white rounded-xl px-4 py-2 text-sm font-semibold"
              style={{ background: "var(--accent)" }}
            >
              {t("emptyCta")}
            </a>
          </div>
        ) : data ? (
          <div className="space-y-8">
            {/* Stat cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard
                label={t("stats.level")}
                value={data.overall.latest_overall != null ? String(data.overall.latest_overall) : "–"}
                sub={data.overall.band ? t(`band.${data.overall.band}`) : undefined}
              />
              <StatCard label={t("stats.sessions")} value={String(data.stats.evaluated_sessions)} />
              <StatCard label={t("stats.practice")} value={practiceLabel} />
              <StatCard label={t("stats.patients")} value={String(data.stats.patients_count)} />
            </div>

            {/* Hero composite line */}
            <section className="rounded-2xl border p-5" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
              <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                <h2 className="font-semibold text-[var(--text-primary)]" style={{ fontFamily: "var(--font-lora)" }}>
                  {t("composite.title")}
                </h2>
                <span className="inline-flex items-center gap-1.5 text-xs text-[var(--text-muted)]">
                  <span className="w-4 h-0 border-t-2 border-dashed" style={{ borderColor: "var(--accent)" }} />
                  {t("composite.competentLine")}
                </span>
              </div>
              <LineChart series={compositeSeries} />
              <p className="text-xs text-[var(--text-muted)] mt-2">{t("composite.caption")}</p>
            </section>

            {/* Skill rings */}
            {data.overall.recent_avg && (
              <section className="rounded-2xl border p-5" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
                <h2 className="font-semibold text-[var(--text-primary)] mb-1" style={{ fontFamily: "var(--font-lora)" }}>
                  {t("skills.title")}
                </h2>
                <p className="text-xs text-[var(--text-secondary)] mb-5">
                  {data.overall.early_avg ? t("skills.legend") : t("skills.legendSingle")}
                </p>
                <SkillRings
                  recent={data.overall.recent_avg}
                  early={data.overall.early_avg}
                  skillLabel={(k) => tSkill(k)}
                  recentLabel={t("skills.recentLabel")}
                  earlyLabel={t("skills.earlyLabel")}
                />
              </section>
            )}

            {/* Per-patient */}
            {data.patients.length > 0 && (
              <section>
                <h2 className="font-semibold text-[var(--text-primary)] mb-1" style={{ fontFamily: "var(--font-lora)" }}>
                  {t("patients.title")}
                </h2>
                <p className="text-sm text-[var(--text-secondary)] mb-4">{t("patients.subtitle")}</p>
                <div className="space-y-3">
                  {data.patients.map((p) => <PatientCard key={p.case_id} p={p} />)}
                </div>
              </section>
            )}
          </div>
        ) : null}
      </main>
    </AppShell>
  );
}
